import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Home, CreditCard, Target, MoreHorizontal, Wallet, BarChart3, Settings, Tag, Plus, TrendingUp, TrendingDown, User, ArrowLeftRight, Repeat } from "lucide-react";
import { TransactionForm } from "@/components/transactions/TransactionForm";
import TransferModal from "@/components/forms/TransferModal";
import { cn } from "@/lib/utils";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { supabase } from "@/integrations/supabase/client";
import { Conta } from "@/types/database";
import { useAuth } from "@/hooks/useAuth";
import { FloatingActionMenu } from "./FloatingActionMenu";

interface BottomNavigationProps {
  currentTab: 'transactions' | 'cards' | 'planning' | 'accounts' | 'reports';
  onTabChange: (tab: 'transactions' | 'cards' | 'planning' | 'accounts' | 'reports') => void;
  onTransactionSuccess?: () => void;
  onOpenAssistant?: () => void;
}

const mainItems = [
  { id: "dashboard", label: "Principal", icon: Home },
  { id: "transactions", label: "Transações", icon: CreditCard },
  { id: "add", label: "", icon: Plus, special: true },
  { id: "reports", label: "Relatórios", icon: BarChart3 },
  { id: "more", label: "Mais", icon: MoreHorizontal },
];

const moreItems = [
  { id: "accounts", label: "Contas", icon: Wallet },
  { id: "credit-cards", label: "Cartões", icon: CreditCard },
  { id: "goals", label: "Metas", icon: Target },
  { id: "settings-menu", label: "Configurações", icon: Settings },
];

const settingsSubItems = [
  { id: "categories", label: "Categorias", icon: Tag },
  { id: "profile", label: "Perfil", icon: User },
  { id: "help", label: "Ajuda", icon: () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg> },
];

export function BottomNavigation({
  currentTab,
  onTabChange,
  onTransactionSuccess,
  onOpenAssistant,
}: BottomNavigationProps) {
  const { openModal } = useProfileModal();
  const { user } = useAuth();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isReceitaOpen, setIsReceitaOpen] = useState(false);
  const [isDespesaOpen, setIsDespesaOpen] = useState(false);
  // Novo estado para controlar se a despesa é do tipo cartão
  const [isDespesaCartao, setIsDespesaCartao] = useState(false);
  const [contas, setContas] = useState<Conta[]>([]);

  // Buscar contas do usuário
  useEffect(() => {
    const fetchContas = async () => {
      if (!user?.id) return;

      // Se o modal de transferência não estiver aberto, não precisa buscar
      if (!isTransferOpen) return;
      
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('user_id', user.id)
        .order('nome');
      
      if (error) {
        console.error("Erro ao buscar contas:", error);
        return;
      }

      setContas((data as Conta[]) || []);
    };
    
    fetchContas();
  }, [user?.id, isTransferOpen]);

  const handleTransferSuccess = () => {
    onTransactionSuccess();
  };

  const handleTabSelect = (tabId: string) => {
    if (tabId === "more") {
      setIsMoreOpen(true);
      return;
    }
    if (tabId === "add") {
      setIsAddOpen(!isAddOpen);
      return;
    }
    onTabChange(tabId);
  };

  const handleMoreItemSelect = (tabId: string) => {
    if (tabId === "settings-menu") {
      setIsMoreOpen(false);
      setIsSettingsOpen(true);
      return;
    }
    onTabChange(tabId);
    setIsMoreOpen(false);
  };

  const handleSettingsItemSelect = (tabId: string) => {
    if (tabId === "profile") {
      setIsSettingsOpen(false);
      openModal();
      return;
    }
    if (tabId === "help") {
      setIsSettingsOpen(false);
      window.open("https://github.com/leandrofreitas/uni-financas-desk/wiki/ajuda", "_blank");
      return;
    }
    onTabChange(tabId);
    setIsSettingsOpen(false);
  };

  const isMainTabActive = (tabId: string) => {
    if (tabId === "more") {
      return moreItems.some(item => item.id === currentTab) || 
             settingsSubItems.some(item => item.id === currentTab);
    }
    if (tabId === "add") {
      return false; // Never active since it's an action button
    }
    return currentTab === tabId;
  };

  return (
    <>
      {/* Floating Action Menu */}
      <FloatingActionMenu
        isOpen={isAddOpen}
        onToggle={() => setIsAddOpen(!isAddOpen)}
        onReceita={() => {
          setIsAddOpen(false);
          setIsReceitaOpen(true);
        }}
        onDespesa={() => {
          setIsAddOpen(false);
          setIsDespesaCartao(false);
          setIsDespesaOpen(true);
        }}
        onTransferencia={() => {
          setIsAddOpen(false);
          setIsTransferOpen(true);
        }}
        onAssistant={() => {
          setIsAddOpen(false);
          onOpenAssistant();
        }}
      />

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 h-16 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t border-border z-30">
        <div className="flex items-center justify-around h-full px-2">
          {mainItems.map((item) => {
            const Icon = item.icon;
            const isActive = isMainTabActive(item.id);
            const isSpecial = item.special;
            
            // Se for o botão especial (+), renderizamos um placeholder invisível para manter o espaçamento
            // pois o botão real está no FloatingActionMenu
            if (isSpecial) {
              return (
                <div key={item.id} className="w-12 h-12 mx-auto" />
              );
            }
            
            return (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                onClick={() => handleTabSelect(item.id)}
                className={cn(
                  "flex flex-col items-center space-y-1 min-w-[64px] h-full transition-colors mobile-button-fix",
                  isActive 
                    ? "text-primary bg-primary/10 rounded-lg border border-primary/20 shadow-sm [&]:active:!bg-primary/10 [&]:active:!text-primary [&]:focus:!bg-primary/10 [&]:focus:!text-primary" 
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg [&]:active:!bg-accent [&]:active:!text-foreground [&]:focus:!bg-accent [&]:focus:!text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Sheet para "Mais" */}
      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent side="bottom" className="h-[400px]">
          <SheetHeader>
            <SheetTitle>Mais opções</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-3 mt-6 px-2">
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  onClick={() => handleMoreItemSelect(item.id)}
                  className="flex flex-col items-center justify-center space-y-2 h-16 text-center"
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-xs font-medium">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Sheet para "Configurações" */}
      <Sheet open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <SheetContent side="bottom" className="h-[300px]">
          <SheetHeader>
            <SheetTitle>Configurações</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {settingsSubItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  onClick={() => handleSettingsItemSelect(item.id)}
                  className="flex flex-col items-center space-y-2 h-20"
                >
                  <Icon className="h-8 w-8" />
                  <span className="text-sm">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de Transferência */}
       <TransferModal 
         open={isTransferOpen}
         onOpenChange={setIsTransferOpen}
         onAfterTransfer={handleTransferSuccess}
         contas={contas}
       />

      {/* Modal de Receita */}
      <Sheet open={isReceitaOpen} onOpenChange={setIsReceitaOpen}>
        <SheetContent side="bottom" className="h-[90vh]">
          <SheetHeader>
            <SheetTitle className="text-green-600 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Nova Receita
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <TransactionForm 
              tipo="entrada" 
              asDialog={false}
              isOpen={isReceitaOpen}
              onSuccess={() => {
                setIsReceitaOpen(false);
                onTransactionSuccess();
              }} 
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de Despesa */}
      <Sheet open={isDespesaOpen} onOpenChange={setIsDespesaOpen}>
        <SheetContent side="bottom" className="h-[90vh]">
          <SheetHeader>
            <SheetTitle className="text-red-600 flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              {isDespesaCartao ? "Nova Despesa (Cartão)" : "Nova Despesa"}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <TransactionForm 
              tipo="saida" 
              asDialog={false}
              isOpen={isDespesaOpen}
              onSuccess={() => {
                setIsDespesaOpen(false);
                onTransactionSuccess();
              }}
              initialContaTipo={isDespesaCartao ? "cartao" : "conta"}
            />
          </div>
        </SheetContent>
      </Sheet>

    </>
  );
}
